// The form's mirror of src/modules/intake/intake/answers.ts on the server: which
// questions the chosen case type asks, which of them are required and
// what is still outstanding. The server stays the judge; this only
// decides what to render.

import type {
  IntakeChecklistItem,
  IntakeLanguage,
  IntakeQuestion,
  IntakeTemplate,
  IntakeText,
} from "@duralex/shared";

function groupOf(template: IntakeTemplate, category: string | null): string | null {
  return template.categories.find((c) => c.code === category)?.group ?? null;
}

function covers(codes: string[], category: string | null, group: string | null): boolean {
  if (codes.includes("ALL")) return true;
  if (!category) return false;
  return codes.includes(category) || (group !== null && codes.includes(group));
}

export function applicableQuestions(
  template: IntakeTemplate,
  category: string | null,
): IntakeQuestion[] {
  const group = groupOf(template, category);
  return template.sections
    .flatMap((s) => s.questions)
    .filter((q) => covers(q.appliesTo, category, group));
}

export function applicableChecklist(
  template: IntakeTemplate,
  category: string | null,
): IntakeChecklistItem[] {
  const group = groupOf(template, category);
  return template.checklist.filter((d) => covers(d.appliesTo, category, group));
}

export function isRequired(
  q: { required: string; requiredFor?: string[] },
  template: IntakeTemplate,
  category: string | null,
): boolean {
  if (q.required !== "yes") return false;
  if (!q.requiredFor) return true;
  return covers(q.requiredFor, category, groupOf(template, category));
}

/** Blank is no answer: a cleared field, an emptied list. */
export function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  return Array.isArray(value) && value.length === 0;
}

/** What still stands between this client and submitting. */
export function outstanding(
  template: IntakeTemplate,
  category: string | null,
  answers: Record<string, unknown>,
  uploadedItems: Iterable<string>,
): { questions: string[]; documents: string[] } {
  const uploaded = new Set(uploadedItems);
  return {
    questions: applicableQuestions(template, category)
      .filter((q) => isRequired(q, template, category) && isBlank(answers[q.id]))
      .map((q) => q.id),
    documents: applicableChecklist(template, category)
      .filter((d) => isRequired(d, template, category) && !uploaded.has(d.id))
      .map((d) => d.id),
  };
}

// ---------------------------------------------------------------------------
// The editor's mirror of src/modules/intake/intake/template.ts on the server: which
// languages the form may offer. All-or-nothing per language, as there.

export const INTAKE_LANGUAGES: IntakeLanguage[] = ["en", "ru", "uk", "uz", "ka", "tr", "es"];

type TemplateArrays = Pick<IntakeTemplate, "categories" | "sections" | "checklist" | "consents">;

/** Every label in the template, keyed the way the server's i18n files name them. */
export function templateLabels(t: TemplateArrays): Array<{ key: string; text: IntakeText }> {
  const out: Array<{ key: string; text: IntakeText }> = [];
  for (const c of t.categories) out.push({ key: `cat.${c.code}`, text: c.label });
  for (const s of t.sections) {
    out.push({ key: `section.${s.id}`, text: s.title });
    for (const q of s.questions) {
      out.push({ key: `q.${q.id}`, text: q.label });
      if (q.help) out.push({ key: `q.${q.id}.help`, text: q.help });
      for (const o of q.options ?? []) out.push({ key: `opt.${q.id}.${o.value}`, text: o.label });
    }
  }
  for (const d of t.checklist) out.push({ key: `doc.${d.id}`, text: d.label });
  for (const c of t.consents) out.push({ key: `consent.${c.id}`, text: c.text });
  return out;
}

/** Labels without each language; a language the form may offer has 0. */
export function missingTranslations(t: TemplateArrays): Record<IntakeLanguage, number> {
  const labels = templateLabels(t);
  const out = {} as Record<IntakeLanguage, number>;
  for (const lang of INTAKE_LANGUAGES) {
    out[lang] = labels.filter((l) => !l.text[lang]).length;
  }
  return out;
}

/** The draft's translations that are out of date, per language: a
    translation left as it was loaded while its English moved - in this
    draft, or in an earlier save the server still remembers. A retyped or
    new translation is current: the save records it afresh. A label whose
    id moved (an option value fixed) is the loaded label that said the
    same in both languages, as the server will read it. Keys in the
    draft's own order. */
export function staleTranslations(
  loaded: TemplateArrays,
  health: Partial<Record<IntakeLanguage, { stale: string[] }>>,
  draft: TemplateArrays,
): Record<IntakeLanguage, string[]> {
  const before = templateLabels(loaded);
  const now = templateLabels(draft);
  const was = new Map(before.map((l) => [l.key, l.text]));
  const keys = new Set(now.map((l) => l.key));
  const out = {} as Record<IntakeLanguage, string[]>;
  for (const lang of INTAKE_LANGUAGES) {
    const saved = new Set(health[lang]?.stale ?? []);
    out[lang] = [];
    if (lang === "en") continue;
    // The labels that left the draft, by what they said in both languages:
    // a queue spent one taker at a time, in the server's order (its records
    // are stored sorted by key, not in the form's order).
    const left = new Map<string, { key: string; text: IntakeText }[]>();
    for (const l of [...before].sort((x, y) => (x.key < y.key ? -1 : x.key > y.key ? 1 : 0))) {
      if (keys.has(l.key) || !l.text[lang]) continue;
      const k = `${l.text.en}\u0000${l.text[lang]}`;
      left.set(k, [...(left.get(k) ?? []), l]);
    }
    for (const { key, text } of now) {
      const said = text[lang];
      if (!said) continue;
      // A cell the loaded copy translated is itself, retyped or not; only a
      // cell with no reading of its own may be a label that moved.
      const own = was.get(key);
      const prev = own?.[lang] ? { key, text: own } : left.get(`${text.en}\u0000${said}`)?.shift();
      if (!prev || prev.text[lang] !== said) continue;
      // The server hashes the English trimmed: a stray space moves nothing.
      if (prev.text.en.trim() !== text.en.trim() || saved.has(prev.key)) out[lang].push(key);
    }
  }
  return out;
}

/** How far one language is behind in a part of the form: cells still
    empty, and cells whose English moved since they were written. */
export interface Behind {
  untranslated: number;
  outOfDate: number;
}

/** The count for one part of the form - a tab, or a single section -
    read with the whole form's key grammar, so the parts add up to the
    language's own numbers. */
export function behindIn(
  part: Partial<TemplateArrays>,
  lang: IntakeLanguage,
  stale: Set<string>,
): Behind {
  const labels = templateLabels({
    categories: [],
    sections: [],
    checklist: [],
    consents: [],
    ...part,
  });
  return {
    untranslated: labels.filter((l) => !l.text[lang]).length,
    outOfDate: labels.filter((l) => stale.has(l.key)).length,
  };
}
